import type { PropertyValues } from 'lit-element'
import { query, LitElement, html, property, state } from 'lit-element'
import type { TemplateResult } from 'lit-html'
import { nothing } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import { SVG_PERSON_OUTLINED } from '@getgo/chameleon-icons'

import styles from './avatar.styles.scss'
import { getAvatarUrls } from '../../services/avatar'

import { AvatarVariant, IconBadgeVariant } from '@getgo/chameleon-core'
import type { AvatarSize, AvatarType } from '@getgo/chameleon-core'
import type { AvatarProps } from './models'
import { GOTO_AVATAR } from './models'
import { noop } from '../../common/helpers'
import { getDocument } from '../../common/dom-helpers'
import { getAvatarColorTokenFromString } from './avatar-color-helper'
import type { AvatarComponent } from '@getgo/chameleon-web'

/**
 * @see react/avatar/avatar.shell.tsx
 */
export class GoToAvatar extends LitElement implements AvatarProps {
  static readonly tagName = GOTO_AVATAR
  @query('chameleon-avatar') chameleonAvatar: AvatarComponent | undefined
  @state() protected avatar: HTMLImageElement | string | TemplateResult | undefined
  @state() private avatarColorToken = '--goto-avatar-color-primary'
  @property({ type: Boolean }) showPresence = false
  @property({ type: Boolean }) hidePresenceTooltip = false
  @property({ type: Boolean }) usePresenceSnapshot = false
  @property({ type: String }) size: AvatarSize | undefined
  @property({ type: String }) type: AvatarType | undefined
  @property({ type: String }) givenName = ''
  @property({ type: String }) familyName = ''
  @property({ type: String }) profileImageUrl = ''
  @property({ type: String }) externalUserKey: string | undefined = undefined
  @property({ type: String }) icon = ''
  @property({ type: String }) emailAddress = ''
  @property({ type: String }) variant?: AvatarVariant
  @property({ type: String }) iconBadge?: IconBadgeVariant
  @property({ attribute: false }) onClick = noop
  @property({ type: String }) label? = `${this.givenName} ${this.familyName}`

  static get styles() {
    return styles
  }

  connectedCallback() {
    super.connectedCallback()
    this.setColorToken()
    this.setBackgroundColor()
    this.getAvatar()
  }

  update(changedProperties: PropertyValues) {
    if (!changedProperties.get('avatar')) {
      this.getAvatar()
    }
    super.update(changedProperties)
  }

  protected createRenderRoot(): Element | ShadowRoot {
    return this.attachShadow({ mode: 'open', delegatesFocus: true })
  }

  private getFirstChar(name: string) {
    return name?.trim().charAt(0)
  }

  private setBackgroundColor() {
    if (this.variant === 'primary') {
      // necessary due to a race condition in the setting of the primary background color token in chameleon
      setTimeout(() =>
        this.chameleonAvatar?.style.setProperty('--goto-avatar-bg-color', `var(${this.avatarColorToken})`),
      )
    }
  }

  private getInitials() {
    const firstInitial = this.givenName ?? ''
    const lastInitial = this.familyName ?? ''
    return `${this.getFirstChar(firstInitial)}${this.getFirstChar(lastInitial)}`.toUpperCase()
  }

  private setColorToken() {
    const stringForColor = this.externalUserKey
      ? this.externalUserKey
      : this.givenName?.concat(this.familyName) ?? this.familyName

    if (!this.givenName && !this.familyName) {
      this.avatarColorToken = '--goto-avatar-color-primary'
    } else if (stringForColor) {
      this.avatarColorToken = getAvatarColorTokenFromString(stringForColor)
    }
  }

  private getFallbackAvatar() {
    if (this.givenName || this.familyName) {
      return this.getInitials()
    }
    return this.getPersonIconTemplate()
  }

  updated() {
    this.setColorToken()
    this.setBackgroundColor()
  }

  private async getProfileImageUrl(externalUserKey = '', emailAddress?: string) {
    const { urlMedium, isDefault = true } = await getAvatarUrls(externalUserKey, emailAddress)
    if (isDefault) {
      throw 'using default'
    }
    return urlMedium
  }

  /**
   * The goto-avatar should render 1 of 4 different templates depending on the given properties and the user's
   * profile image setting:
   *  - icon tag
   *  - img tag
   *  - initials string
   *  - person icon svg
   *
   * When a profileImageUrl is given:
   *  - if the img successfully loads, render the img tag.
   *  - if the img fails to load, do fallback flow.
   *
   * When externalUserKey or emailAddress is given, and profileImageUrl is omitted:
   *  - if the user has uploaded a custom profile image (isDefault = false) render the img tag.
   *  - if the user is using the default profile image (isDefault = true), do fallback flow.
   *  - if the user has uploaded a custom profile image, but loading the image fails, do fallback flow.
   *
   * Fallback flow:
   *  - if we know the user's given or last name, render the intials string, else proceed to step 2.
   *  - if we know nothing about the user, display the person icon svg.
   */
  private async getAvatar() {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (this.profileImageUrl || this.externalUserKey || this.emailAddress) {
      try {
        const profileImageUrl =
          this.profileImageUrl || (await this.getProfileImageUrl(this.externalUserKey, this.emailAddress))
        this.avatar = this.createImage(profileImageUrl)
      } catch (error) {
        this.avatar = this.getFallbackAvatar()
      }
    } else {
      this.avatar = this.getFallbackAvatar()
    }
  }

  private createImage(url: string) {
    const img = getDocument().createElement('img')
    img.alt = `${this.givenName} ${this.familyName}`
    img.src = url
    img.onerror = () => (this.avatar = this.getFallbackAvatar())

    return img
  }

  private getPersonIconTemplate() {
    return html`<chameleon-svg data-test-person-icon> ${unsafeSVG(SVG_PERSON_OUTLINED)} </chameleon-svg>`
  }

  render() {
    return html`
      <chameleon-avatar
        size=${ifDefined(this.size)}
        type=${ifDefined(this.type)}
        @click=${this.onClick}
        data-test="avatar"
        variant=${ifDefined(this.variant)}
        label=${ifDefined(this.label)}
      >
        ${this.icon ? html`<chameleon-svg>${unsafeSVG(this.icon)}</chameleon-svg>` : this.avatar}
        ${this.iconBadge
          ? html`<chameleon-icon-badge
              type=${this.iconBadge}
              size="small"
              slot="badge"
              label=${this.iconBadge}
            ></chameleon-icon-badge>`
          : nothing}
        ${this.showPresence && !!this.externalUserKey
          ? html`<goto-presence-indicator
              tabindex="-1"
              slot="presence"
              .externalUserKey=${this.externalUserKey}
              .presenceSnapshot=${this.usePresenceSnapshot}
              .hidePresenceTooltip=${this.hidePresenceTooltip}
            />`
          : nothing}
      </chameleon-avatar>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToAvatar.tagName]: GoToAvatar
  }
}
